import React from 'react';
import { useLanguage } from '@/i18n';
import { KeenIcon, Menu, MenuItem, MenuToggle } from '@/components';
import { useEffect, useState } from 'react';
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import settingService from '@/services/setting/settingService';
import { toast } from 'sonner';
import { useAbility } from '@/providers';
import { Can } from '@casl/react';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import { describeRoleSaveError, selectAllPermissions } from '@/pages/account/members/roles/roleForm';

const validationSchema = Yup.object({
  name: Yup.string().required("Role name is required"),
  permissions: Yup.array()
    .of(Yup.object().shape({ name: Yup.string().required() }))
    .test(
      "admin-or-valid-permissions",
      "Select at least one permission or Administrator access",
      (value) => {
        if (!value || value.length === 0) return false;

        const hasIsAdmin = value.some(p => p.name === "is_admin_access");
        const otherPermissions = value.filter(p => p.name !== "is_admin_access");

        return hasIsAdmin && otherPermissions.length === 0 || otherPermissions.length > 0;
      }
    )
    .test(
      "require-view-permissions-for-contact",
      "Please select either 'View All Contacts' or 'View Assigned Contacts'",
      (value) => {
        const names = value?.map(p => p.name) || [];
        if (names.includes("access-contact")) {
          return names.includes("view-all-contact") || names.includes("view-assigned-contact");
        }
        return true;
      }
    )
    .test(
      "require-view-permissions-for-matter",
      "Please select either 'View All Matters' or 'View Assigned Matters'",
      (value) => {
        const names = value?.map(p => p.name) || [];
        if (names.includes("access-matter")) {
          return names.includes("view-all-matter") || names.includes("view-assigned-matter");
        }
        return true;
      }
    )
    .test(
      "require-view-permissions-for-invoice",
      "Please select either 'Read only Invoice' or 'Manage Invoice'",
      (value) => {
        const names = value?.map(p => p.name) || [];
        if (names.includes("access-invoice")) {
          return names.includes("readonly-invoice") || names.includes("manage-invoice");
        }
        return true;
      }
    )
});

const CardRole = ({ itemId, title, subTitle, description, team, badge, getAllRoles, dataLoading, data, openRoleModal }) => {
  const { ability } = useAbility();
  const { isRTL } = useLanguage();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [permissionData, setPermissionData] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
  const [initialValues, setInitialValues] = useState({
    name: "",
    active: true,
    permissions: [],
  });
  const canManageRole = ability.can('handle', 'role')

  const handleSelectAll = (e, values, setFieldValue) => {
    const { checked } = e.target;
    setFieldValue("permissions", checked ? selectAllPermissions(permissionData) : []);
    setSelectAll(checked);
  };

  // collect all related permission names for a parent
  const collectRelatedNames = (perm) => {
    const names = [perm.name];
    if (perm.options?.length) names.push(...perm.options.map(o => o.name));
    if (perm.children?.length) names.push(...perm.children.map(c => c.name));
    return names;
  };

  // remove an array of names from the current selection
  const removePermissionsByNames = (selected, namesToRemove) => {
    const toRemove = new Set(namesToRemove);
    return selected.filter(p => !toRemove.has(p.name));
  };

  const handleEditRole = async (id) => {
    setCurrentItemId(id);
    setShowRoleModal(true);
    setIsLoading(true);
    try {
      const [groupRes, roleRes] = await Promise.all([
        settingService.getPermisionsGroup(),
        settingService.getSingleRole(id),
      ]);
      if (groupRes.status) {
        const filtered = Object.fromEntries(
          Object.entries(groupRes.data).filter(([key]) => key !== "referralAttorney")
        );
        setPermissionData(filtered);
      }
      if (roleRes.status) {
        const assignedPermissions = roleRes.data.permissions;
        setInitialValues((prev) => ({
          ...prev,
          name: roleRes.data.name.replace(/-/g, ' ') || "",
          active: roleRes.data.active || false,
          permissions: assignedPermissions || [],
        }));
      }
    } catch (error) {
      console.log("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitRoles = async (values, resetForm, setErrors) => {
    const isAdminSelected = values.permissions.some(p => p.name === 'is_admin_access');

    const updatedRoleData = {
      name: values.name.replace(/\s+/g, '-'),
      active: values.active,
      permissions: isAdminSelected
        ? ['is_admin_access']
        : values.permissions.map(permission => permission.name),
    };
    setButtonLoading(true);
    try {
      const { status, data } = await settingService.updateRole(updatedRoleData, currentItemId);
      if (status) {
        setCurrentItemId(null);
        setShowRoleModal(false);
        getAllRoles();
        toast.success("Role updated successfully", {
          description: `${data.name.replace(/[-\s]/g, " ")} Role updated successfully.`,

        });
      }
    } catch (error) {
      console.log("Error:", error);
      const { fieldErrors, message } = describeRoleSaveError(error, "An error occurred while updating role. Please try again later.");
      setErrors(fieldErrors);
      toast.error("Failed to update role.", {
        description: message,
        action: {
          label: "Ok",
        },
      });
    } finally {
      setButtonLoading(false);
    }
  };

  const handleDelete = async () => {
    closeModal();
    try {
      const { status } = await settingService.deleteRole(actionId);
      if (status) {
        toast.success("Role Deleted successfully", {
          description: `Role deleted successfully.`,

        });
      }
    } catch (error) {
      console.log("Error:", error);
    } finally {
      setDeleteLoading(false);
      getAllRoles();
    }
  };


  const preprocessPermissions = (permissions) => {
    return permissions.map((perm) => {
      // If this is "invoice", inject "manage-invoice" into options
      if (perm.name === "access-invoice") {
        const hasManageInOptions = perm.options?.some(opt => opt.name === "manage-invoice");
        if (!hasManageInOptions) {
          // Find and remove "manage-invoice" from children
          const manageChild = perm.children?.find(child => child.name === "manage-invoice");
          if (manageChild) {
            perm.options = [...(perm.options || []), manageChild];
            perm.children = perm.children.filter(child => child.name !== "manage-invoice");
          }
        }
      }
      if (perm.name === "access-mass-mailing") {
        const hasManageInOptions = perm.options?.some(opt => opt.name === "manage-mass-mailing");
        if (!hasManageInOptions) {
          // Find and remove "manage-mass-mailing" from children
          const manageChild = perm.children?.find(child => child.name === "manage-mass-mailing");
          if (manageChild) {
            perm.options = [...(perm.options || []), manageChild];
            perm.children = perm.children.filter(child => child.name !== "manage-mass-mailing");
          }
        }
      }
      if (perm.name === "access-intake-form") {
        const hasManageInOptions = perm.options?.some(opt => opt.name === "manage-intake-form");
        if (!hasManageInOptions) {
          // Find and remove "manage-intake-form" from children
          const manageChild = perm.children?.find(child => child.name === "manage-intake-form");
          if (manageChild) {
            perm.options = [...(perm.options || []), manageChild];
            perm.children = perm.children.filter(child => child.name !== "manage-intake-form");
          }
        }
      }
      if (perm.name === "access-document-template") {
        const hasManageInOptions = perm.options?.some(opt => opt.name === "manage-document-template");
        if (!hasManageInOptions) {
          // Find and remove "manage-intake-form" from children
          const manageChild = perm.children?.find(child => child.name === "manage-document-template");
          if (manageChild) {
            perm.options = [...(perm.options || []), manageChild];
            perm.children = perm.children.filter(child => child.name !== "manage-document-template");
          }
        }
      }
      return perm;
    });
  };

  const renderPermissionGroup = (groupName, permissions, values, setFieldValue) => {
    if (!Array.isArray(permissions)) return null;
    const isAdminSelected = values.permissions.some(
      (perm) => perm.name === "is_admin_access"
    );
    const isChecked = (name) => values.permissions.some(p => p.name === name);
    const isDisabled = (permName) => {
      const selected = values.permissions.map(p => p.name);
      const isAdminOnlySelected = selected.length === 1 && selected[0] === "is_admin_access";
      return isAdminOnlySelected && permName !== "is_admin_access";
    };
    const handleCheck = (checked, name) => {
      let updated = [...values.permissions];
      if (checked) {
        updated.push({ name });
      } else {
        updated = updated.filter(p => p.name !== name);
      }
      setFieldValue("permissions", updated);
    };

    const handleRadio = (groupNames, selectedName) => {
      const updated = values.permissions.filter(p => !groupNames.includes(p.name));
      updated.push({ name: selectedName });
      setFieldValue("permissions", updated);
    };

    return (
      <React.Fragment key={groupName}>
        <tr>
          <td className="!py-5.5 font-bold text-md capitalize">
            {groupName
              .replace(/([a-z])([A-Z])/g, '$1 $2')
              .replace(/-/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
            }
          </td>
          <td colSpan={6}></td>
        </tr>

        {permissions.map((perm) => {
          const isPermChecked = isChecked(perm.name);
          const hasOptions = perm.options?.length > 0;
          const hasChildren = perm.children?.length > 0;

          return (
            <React.Fragment key={perm.name}>
              {/* Parent Checkbox */}
              <tr>
                <td className="pl-8 text-sm font-medium text-gray-700">{perm.label}</td>
                <td colSpan={6} className="text-right">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-input"
                    checked={isPermChecked}
                    value={perm.name}
                    disabled={isDisabled(perm.name)}
                    // onChange={(e) => handleCheck(e.target.checked, perm.name)}
                    onChange={(e) => {
                      const { checked } = e.target;
                      let updated = [...values.permissions];

                      if (checked) {
                        // add only the parent if not already present
                        if (!isPermChecked) updated.push({ name: perm.name });
                      } else {
                        // parent unchecked ⇒ remove parent + all its options + all its children
                        const allNames = collectRelatedNames(perm);
                        updated = removePermissionsByNames(updated, allNames);
                      }

                      setFieldValue("permissions", updated);
                    }}
                  />
                </td>
              </tr>

              {/* Radio options (if parent is checked and options exist) */}
              {isPermChecked && hasOptions && (
                <tr>
                  <td className="pl-12 text-sm text-gray-600">Options</td>
                  <td colSpan={6} className="text-right">
                    {perm.options.map((opt) => {
                      const isOptChecked = isChecked(opt.name);

                      return (
                        <label key={opt.name} className="flex items-center justify-end gap-2 mb-1.5">
                          <span className="ml-1">{opt.label}</span>
                          <input
                            type="checkbox"
                            className="checkbox checkbox-sm checkbox-input"
                            checked={isOptChecked}
                            value={opt.name}
                            disabled={isDisabled(opt.name)}
                            onChange={() => {
                              // Ensure only one of the options can be selected at a time
                              const updated = values.permissions.filter(
                                (p) => !perm.options.map(o => o.name).includes(p.name)
                              );

                              // Add the newly selected one (if not already checked)
                              if (!isOptChecked) {
                                updated.push({ name: opt.name });
                              }

                              setFieldValue("permissions", updated);
                            }}
                          />
                        </label>
                      );
                    })}
                  </td>
                </tr>
              )}

              {hasChildren &&
                perm.children.map((child) => {
                  let showChild = false;

                  if (!hasOptions) {
                    showChild = true;
                  } else {
                    // Show children for invoice if manage-invoice is selected
                    if (perm.name === "access-invoice") {
                      showChild = isChecked("manage-invoice");
                    }

                    // Show children for contact and matter if any view-* option is selected
                    else if (["access-contact", "access-matter"].includes(perm.name)) {
                      showChild = perm.options.some(opt => opt.name.startsWith("view-") && isChecked(opt.name));
                    }

                    // For other permissions, show children if any option is checked
                    else {
                      showChild = perm.options.some(opt => isChecked(opt.name));
                    }
                  }

                  return showChild ? (
                    <tr key={child.name}>
                      <td className="pl-16 text-sm text-gray-600">{child.label}</td>
                      <td colSpan={6} className="text-right">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm checkbox-input"
                          value={child.name}
                          disabled={isDisabled(child.name)}
                          checked={isChecked(child.name)}
                          onChange={(e) => handleCheck(e.target.checked, child.name)}
                        />
                      </td>
                    </tr>
                  ) : null;
                })}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setCurrentItemId(null);
    setSelectAll(false)
    setInitialValues({
      name: "",
      active: true,
      permissions: [],
    })
  };

  const renderItem = (item, index) => {
    return (
      <tr key={index}>
        <td className="w-48 min-w-48">
          <div className="flex items-center gap-2.5">
            <KeenIcon icon="user" />
            <div className="mb-px text-sm font-medium text-gray-900 capitalize">{item.name}</div>
          </div>
        </td>

        <td className="font-normal text-gray-700 ">
          <span className={`badge badge-pill badge-outline gap-1 items-center ${item.active ? 'badge-success' : 'badge-warning'}`}>
            <span className={`badge badge-dot size-1.5 ${item.active ? 'badge-success' : 'badge-warning'}`}></span>
            {item.active ? 'Active' : 'Inactive'}
          </span>
        </td>

        <td className="font-normal text-gray-700 ">
          {item.permissions?.map((perm) => (
            <span key={perm.id} className="text-[10px] badge badge-xs badge-outline mr-1 mb-1">
              {perm.name === 'is_admin_access'
                ? 'Administration'
                : perm.name === 'is_company_admin_access'
                  ? 'Company Administration'
                  : perm.name
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
            </span>
          ))}
        </td>

        <td className="!pr-7.5 min-w-16 text-end">
          {!item.is_current && (
            <div className='flex items-center'>
              {canManageRole && (
                <>
                  <button
                    disabled={item.name === 'user' || item.name === 'company admin'}
                    onClick={() => handleEditRole(item.id)}
                    className="mr-2 btn btn-xs btn-icon btn-icon-lg link">
                    <KeenIcon icon="notepad-edit" className="text-[12px]" />
                  </button>
                  <button
                    disabled={item.name === 'user' || item.name === 'company admin'}
                    onClick={() => openModal(item.id)}
                    className="btn btn-xs btn-icon btn-icon-lg text-danger">
                    <KeenIcon icon="trash" className="text-[12px]" />
                  </button>
                </>
              )}
            </div>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="min-w-full card">
      <div className="card-header">
        <h3 className="card-title">Roles</h3>

        <button className='text-xs btn btn-sm btn-light btn-outline' onClick={openRoleModal}>
          <KeenIcon icon="plus" className="w-fit btn-icon" />
          Add New
        </button>
      </div>

      <div className="card-table scrollable-x-auto">
        <div className="scrollable-auto">
          <table className="table font-medium text-gray-600 align-middle text-2sm">
            <tbody>
              {dataLoading ? (
                <tr>
                  <td colSpan="3" className="text-center text-gray-500">
                    <div className="loader">Loading...</div>
                  </td>
                </tr>
              ) : data && Object.keys(data).length > 0 ? (
                Object.values(data)
                  .filter(item => item.name !== "super admin" && item.name !== "referral attorney")
                  .map((item, index) => renderItem(item, index))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center text-gray-500">
                    No Roles found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Role Modal */}
      {showRoleModal && (
        <div className="modal-wrapper modal-animate-fade">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm, setErrors }) => {
              handleSubmitRoles(values, resetForm, setErrors);
            }}
            enableReinitialize={true}
          >
            {({ setFieldValue, values, errors, setErrors }) => (
              <Form className="modal-animate-scale modal-body w-4/5 max-w-3xl">
                <button
                  type="button"
                  onClick={() => handleCloseRoleModal()}
                  disabled={isLoading}
                  className="modal-close-btn"
                >
                  <KeenIcon icon="cross" />
                </button>

                <h2 className="mb-5 text-lg font-medium text-center text-gray-900">Edit Role</h2>

                {/* Role Name */}
                <div className="mb-4">
                  <label className="mb-1 text-gray-900 form-label">Role Name</label>
                  <Field
                    type="text"
                    name="name"
                    placeholder="Enter Role Name"
                    className="input"
                  />
                  <ErrorMessage name="name" component="div" className="mt-1 text-xs text-red-500" />
                </div>

                <div className="flex flex-wrap items-center justify-between">
                  <div className="flex items-center mb-4">
                    <label htmlFor="selectAll" className="mr-2">Select All</label>
                    <input
                      type="checkbox"
                      id="selectAll"
                      checked={values.permissions.some((permission) => permission.name === "is_admin_access")}
                      onChange={(e) => handleSelectAll(e, values, setFieldValue)}
                      className="cursor-pointer checkbox checkbox-sm"
                    />
                  </div>

                  {/* Active Checkbox */}
                  <div className="mb-4">
                    <Field name="active">
                      {({ field, form }) => (
                        <label className="switch w-fit">
                          <span className="switch-label">Active</span>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={() => form.setFieldValue("active", !field.value)}
                          />
                        </label>
                      )}
                    </Field>
                  </div>
                </div>

                {/* Permissions (Example with checkboxes) */}
                <div className="card">
                  <div className="card-table scrollable-x-auto max-h-[400px]">
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="text-start text-gray-300 font-normal min-w-[300px]">Module</th>
                          <th className="font-normal text-center text-gray-700 min-w-24"></th>
                          {/* <th className="text-start text-gray-300 font-normal min-w-[300px]">Module</th>
                          <th className="font-normal text-center text-gray-700 min-w-24">View</th>
                          <th className="font-normal text-center text-gray-700 min-w-24">Create</th>
                          <th className="font-normal text-center text-gray-700 min-w-24">Edit</th>
                          <th className="font-normal text-center text-gray-700 min-w-24">Delete</th>
                          <th className="font-normal text-center text-gray-700 min-w-24">Import</th>
                          <th className="font-normal text-center text-gray-700 min-w-24">Export</th> */}
                        </tr>
                      </thead>
                      <tbody className="font-medium text-gray-900">
                        {isLoading ? (
                          <tr>
                            <td colSpan="5" className="py-5 text-center">Loading...</td>
                          </tr>
                        ) : permissionData?.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="py-5 text-center">No data available</td>
                          </tr>
                        ) : (
                          Object.entries(permissionData).map(([groupName, permissions]) =>
                            // renderPermissionGroup(groupName, permissions, values, setFieldValue)
                            renderPermissionGroup(groupName, preprocessPermissions(permissions), values, setFieldValue)
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <ErrorMessage name="permissions" component="div" className="mt-1 text-xs text-red-500" />

                <button
                  type="submit"
                  disabled={buttonLoading || isLoading}
                  className="flex justify-center w-full mt-4 btn btn-primary grow"
                >
                  {buttonLoading ? "Please Wait..." : "Submit"}
                </button>
              </Form>
            )}
          </Formik>
        </div>
      )}

      <ConfirmationModal
        show={isOpen}
        onClose={closeModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this role? This action is permanent and cannot be undone. If this permission is assigned to any users, their access to the associated module will be revoked."
        title="Are You Sure?"
      />
    </div>
  );
};

export { CardRole };
